import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { UserCog, Shield, User, Users } from "lucide-react";
import { useUserRole, setUserRole, type UserRole } from "@/hooks/use-user-role";

/**
 * Demo component to switch user roles for testing
 * This should only be visible in demo/development mode
 */
export function DemoRoleSwitcher() {
  const { role, isAdmin } = useUserRole();

  const handleRoleChange = (newRole: UserRole) => {
    setUserRole(newRole);
  };

  const getRoleIcon = (roleType: UserRole) => {
    switch (roleType) {
      case "admin":
        return <Shield className="h-4 w-4" />;
      case "manager":
        return <Users className="h-4 w-4" />;
      case "user":
        return <User className="h-4 w-4" />;
    }
  };

  const getRoleBadgeColor = () => {
    switch (role) {
      case "admin":
        return "bg-green-50 text-green-700 border-green-200";
      case "manager":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "user":
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <UserCog className="h-4 w-4" />
          <span className="hidden sm:inline">Demo Role:</span>
          <Badge variant="outline" className={getRoleBadgeColor()}>
            {getRoleIcon(role)}
            <span className="ml-1 capitalize">{role}</span>
          </Badge>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Switch User Role (Demo)</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => handleRoleChange("admin")} className="gap-2">
          <Shield className="h-4 w-4 text-green-600" />
          <div className="flex-1">
            <div className="font-medium">Administrator</div>
            <div className="text-xs text-muted-foreground">Full access to all settings</div>
          </div>
          {role === "admin" && (
            <Badge variant="secondary" className="ml-2">
              Current
            </Badge>
          )}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleRoleChange("manager")} className="gap-2">
          <Users className="h-4 w-4 text-blue-600" />
          <div className="flex-1">
            <div className="font-medium">Manager</div>
            <div className="text-xs text-muted-foreground">Limited administrative access</div>
          </div>
          {role === "manager" && (
            <Badge variant="secondary" className="ml-2">
              Current
            </Badge>
          )}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleRoleChange("user")} className="gap-2">
          <User className="h-4 w-4 text-gray-600" />
          <div className="flex-1">
            <div className="font-medium">User</div>
            <div className="text-xs text-muted-foreground">Standard user access</div>
          </div>
          {role === "user" && (
            <Badge variant="secondary" className="ml-2">
              Current
            </Badge>
          )}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className="px-2 py-1.5 text-xs text-muted-foreground">
          Page will reload after switching roles
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
